import PejerBase from 'pejer/src'
import { padDate } from 'pejer/src/helpers'
import { ConnectedAccounts } from 'pejer/src/tables/ConnectedAccounts'
import { PejerState } from 'pejer/src/types/base'
import { ApiResponse } from 'pejer/src/types/types'
import { config } from './config'
import { Company, OnBoardingRequest, OnboardingLinkResponse } from './types'

export class PejerUser extends PejerBase<typeof config.schema.tables, typeof config.urls> {
  constructor(state: PejerState) {
    super(state)
  }

  async getConnectedAccountStatitsics(
    accountID: string | null,
    startDate: Date,
    endDate: Date,
    groupByTime: ConnectedAccounts.Statistics.Query['groupByTime'] = 'Day',
    platformID?: string
  ): Promise<ApiResponse<ConnectedAccounts.Statistics[]>> {
    const formatter = new Intl.DateTimeFormat('no-NO', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })
    const startDateFormatted = formatter.formatToParts(startDate).reduce((acc, part) => {
      return part.value + acc
    }, '')
    const endDateFormatted = formatter.formatToParts(endDate).reduce((acc, part) => {
      return part.value + acc
    }, '')

    const urlObject = {
      path:
        accountID != null
          ? this.urls.connectedAccounts.statistics.path.replace('{ConnectedAccountID}', accountID)
          : this.urls.connectedAccounts.statisticsAll.path,
      method: this.urls.connectedAccounts.statistics.method,
    }

    const query: any = { startDate: startDateFormatted, endDate: endDateFormatted, groupByTime }

    if (platformID) query.platformID = platformID

    const res = await this.fetchPejerData<
      ConnectedAccounts.Statistics,
      ConnectedAccounts.Statistics.Query
    >(urlObject, query)

    if ('error' in res) return res

    res.data = res.data.map((x) => {
      const copy = { ...x }
      copy.paidOutCount = x.paidOutCount
      copy.paidOutSum = x.paidOutSum / 100
      copy.revenueSum = x.revenueSum / 100
      return copy
    })

    res.data = padDate(res.data, startDate, endDate)

    return res
  }

  async updateConnectedAccountSettings(
    linkId: string,
    values: Partial<ConnectedAccounts.Settings> & { externalReferenceID: string }
  ) {
    return await this.fetch(this.urls.onBoarding.editAccountSettings, {
      pathArgs: {
        LinkID: linkId,
      },
      body: values,
      ignoreAuthorization: true,
    })
  }

  async getProffData(query: {
    orgID: string
    countryCode: string
    forceRefresh?: boolean
  }): Promise<ApiResponse<Company>> {
    const urlWithParams = this.urls.onBoarding.getProffData.path

    const params = '?' + new URLSearchParams(query as unknown as Record<string, string>)

    return await this.handleFetch(
      () =>
        fetch(this.baseURL + urlWithParams + params, {
          method: this.urls.onBoarding.getProffData.method,
          headers: {
            ...this.getHeaders(),
          },
        }),
      {
        ignoreAuthorization: true,
      }
    )
  }

  async getAccountFromOnBoardingLink(linkID: string): Promise<ApiResponse<OnboardingLinkResponse>> {
    const urlWithParams = this.urls.onBoarding.getAccount.path.replace('{LinkID}', linkID)

    return await this.handleFetch(
      () =>
        fetch(this.baseURL + urlWithParams, {
          method: this.urls.onBoarding.getAccount.method,
          headers: {
            ...this.getHeaders(),
          },
        }),
      {
        ignoreAuthorization: true,
      }
    )
  }
  async getAccountFromConnectedAccountID(
    connectedAccountID: string
  ): Promise<ApiResponse<OnboardingLinkResponse>> {
    const urlWithParams = this.urls.onBoarding.getOverviewInformation.path.replace(
      '{connectedAccountID}',
      connectedAccountID
    )

    return await this.handleFetch(
      () =>
        fetch(this.baseURL + urlWithParams, {
          method: this.urls.onBoarding.getOverviewInformation.method,
          headers: {
            ...this.getHeaders(),
          },
        }),
      {
        ignoreAuthorization: true,
      }
    )
  }
  async getAccountFromSignLink(linkID: string): Promise<ApiResponse<OnboardingLinkResponse>> {
    const urlWithParams = this.urls.onBoarding.getSignAccount.path.replace('{LinkID}', linkID)

    return await this.handleFetch(
      () =>
        fetch(this.baseURL + urlWithParams, {
          method: this.urls.onBoarding.getSignAccount.method,
          headers: {
            ...this.getHeaders(),
          },
        }),
      {
        ignoreAuthorization: true,
      }
    )
  }

  async signOnBoardLink(linkID: string) {
    const urlWithParams = this.urls.onBoarding.signUsingLink.path.replace('{LinkID}', linkID)

    return await this.handleFetch(
      () =>
        fetch(this.baseURL + urlWithParams, {
          method: this.urls.onBoarding.signUsingLink.method,
          headers: {
            ...this.getHeaders(),
          },
        }),
      {
        ignoreAuthorization: true,
      }
    )
  }

  async saveTemporaryOnboarding(linkID: string, data: OnBoardingRequest.Full) {
    const urlWithParams = this.urls.onBoarding.saveTemporary.path.replace('{LinkID}', linkID)

    return await this.handleFetch(
      () =>
        fetch(this.baseURL + urlWithParams, {
          method: this.urls.onBoarding.saveTemporary.method,
          headers: {
            'Content-Type': 'application/json',
            ...this.getHeaders(),
          },
          body: JSON.stringify(data),
        }),
      {
        ignoreAuthorization: true,
      }
    )
  }

  async onBoardAccount(linkID: string, data: OnBoardingRequest.Full) {
    const urlWithParams = this.urls.onBoarding.onBoardAccount.path.replace('{LinkID}', linkID)

    return await this.handleFetch(
      () =>
        fetch(this.baseURL + urlWithParams, {
          method: this.urls.onBoarding.onBoardAccount.method,
          headers: {
            'Content-Type': 'application/json',
            ...this.getHeaders(),
          },
          body: JSON.stringify(data),
        }),
      {
        ignoreAuthorization: true,
      }
    )
  }
}
